import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import {
  MIB,
  TILE_TONE,
  scanError,
  usePlan,
  useStartScan,
  type TileStatus,
} from '../components/site.js';
import { Button, LinkButton, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate, formatDateTime, formatNumber } from '../i18n.js';
import { useMe } from '../session.js';

/** Core Web Vitals thresholds (good, poor) as Google defines them. */
const LIMITS = {
  score: [90, 50],
  lcpMs: [2500, 4000],
  inpMs: [200, 500],
  tbtMs: [200, 600],
  cls: [0.1, 0.25],
  ttfbMs: [800, 1800],
} as const;
type Metric = keyof typeof LIMITS;

export function rate(metric: Metric, value: number | null): TileStatus {
  if (value === null) return 'unknown';
  const [good, poor] = LIMITS[metric];
  // The score is better when higher, all timings when lower.
  if (metric === 'score') return value >= good ? 'good' : value >= poor ? 'warn' : 'bad';
  return value <= good ? 'good' : value <= poor ? 'warn' : 'bad';
}

/**
 * One limit for the page weight on every page, as in the PRD and the rule PERF_PAGE_HEAVY
 * (decision 6, 12.10): 3 MiB. Above it yellow – the rule's problem is a low one (decision 5 of
 * SP-UX10.4). A test holds it to PERF.pageHeavyBytes of packages/rules.
 */
export const HEAVY_MB = 3;

/** The page measured, with its query: `/?page_id=2` is not the home page (review #182). */
export function pageOf(url: string) {
  const u = new URL(url);
  return `${u.pathname}${u.search}`;
}

/** One value of the vitals (site-speed.html .cwv); the first card says what it means. */
function Vital({
  label,
  value,
  status,
  hint,
}: {
  label: string;
  value: string;
  status: TileStatus;
  hint: string | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <span>{label}</span>
      <b className={status === 'unknown' ? undefined : TILE_TONE[status]}>
        {value}
        {/* Not by colour alone (WCAG 1.4.1): the rating is read out with the value. */}
        {status === 'unknown' ? null : (
          <span className="sr-only"> – {t(`tileStatus.${status}`)}</span>
        )}
      </b>
      {hint ? <span>{hint}</span> : null}
    </div>
  );
}

function SnapshotCard({
  siteId,
  snap,
  first,
}: {
  siteId: string;
  snap: Schemas['PerformanceSnapshot'];
  first: boolean;
}) {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const lang = i18n.language;
  // To the hundredth: 0,84 s must not read as the limit 0,8 s it is above.
  const seconds = (ms: number) => `${formatNumber(ms / 1000, lang, 2)} s`;
  const lcp = snap.field?.lcpMs ?? snap.lab.lcpMs;
  const cls = snap.field?.cls ?? snap.lab.cls;
  const inp = snap.field?.inpMs ?? null;
  const path = pageOf(snap.url);
  const hint = (key: string) => (first ? t(`speed.hints.${key}`) : undefined);
  const score = rate('score', snap.score);
  return (
    <section className="card snap">
      <div className="hd">
        <h2>
          {path === '/' ? t('links.homePage') : path} · {t(`speed.strategy.${snap.strategy}`)}{' '}
          <small>
            {snap.field ? t('speed.fieldData') : t('speed.labData')} ·{' '}
            {t('speed.measuredAt', { time: formatDateTime(snap.measuredAt, lang, timeZone) })}
          </small>
        </h2>
        {snap.score !== null ? (
          <span className={`badge ${TILE_TONE[score]} nodot`}>
            {t('speed.scoreOf', { score: snap.score })}
          </span>
        ) : null}
      </div>
      <div className="cwv">
        {lcp !== null ? (
          <Vital label="LCP" value={seconds(lcp)} status={rate('lcpMs', lcp)} hint={hint('lcp')} />
        ) : null}
        {/* INP exists only with data of real visitors; the lab measures TBT (audit). */}
        {inp !== null ? (
          <Vital label="INP" value={`${inp} ms`} status={rate('inpMs', inp)} hint={hint('inp')} />
        ) : null}
        {snap.lab.tbtMs !== null ? (
          <Vital
            label="TBT"
            value={`${snap.lab.tbtMs} ms`}
            status={rate('tbtMs', snap.lab.tbtMs)}
            hint={hint('tbt')}
          />
        ) : null}
        {cls !== null ? (
          <Vital
            label="CLS"
            value={formatNumber(cls, lang, 2)}
            status={rate('cls', cls)}
            hint={hint('cls')}
          />
        ) : null}
        {snap.lab.ttfbMs !== null ? (
          <Vital
            label="TTFB"
            value={seconds(snap.lab.ttfbMs)}
            status={rate('ttfbMs', snap.lab.ttfbMs)}
            hint={hint('ttfb')}
          />
        ) : null}
        {snap.pageBytes !== null ? (
          <Vital
            label={t('speed.weight')}
            value={`${formatNumber(snap.pageBytes / MIB, lang)} MB`}
            status={snap.pageBytes > HEAVY_MB * MIB ? 'warn' : 'good'}
            hint={first ? t('speed.hints.weight', { value: HEAVY_MB }) : undefined}
          />
        ) : null}
      </div>
      {snap.topOpportunities.length > 0 ? (
        <div>
          <b className="h4s">{t('speed.opportunities')}</b>
          <ul className="opps">
            {snap.topOpportunities.map((o) => (
              <li key={o.id}>
                <span>{t(`speed.audits.${o.id}`, { defaultValue: o.id })}</span>
                <b>
                  {o.savingsMs > 0
                    ? t('speed.saves', { value: seconds(o.savingsMs) })
                    : t('speed.saves', { value: `${Math.round(o.savingsBytes / 1024)} KB` })}
                </b>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {first ? (
        <div className="acts">
          {/* The dashboard's tile leads here; the area's problems one step on (decision 11). */}
          <LinkButton to={`/sites/${siteId}/area/performance`} variant="secondary" size="sm">
            {t('speed.view')}
          </LinkButton>
          <LinkButton to={`/sites/${siteId}/help`} variant="primary" size="sm">
            {t('speed.help')}
          </LinkButton>
        </div>
      ) : null}
    </section>
  );
}

/** Page speed (PageSpeed Insights, site-speed.html): Core Web Vitals and the top savings. */
export function Speed() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const pages = usePlan()?.limits.psiUrls;
  const scans = useQuery({
    queryKey: ['site', siteId, 'scans', 'performance'],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/scans', {
          params: { path: { siteId }, query: { type: 'performance', limit: 1 } },
        }),
      ),
    refetchInterval: (query) => {
      const s = query.state.data?.items[0]?.status;
      return s === 'queued' || s === 'running' ? 5000 : false;
    },
  });
  const latestScan = scans.data?.items[0];
  const running = latestScan?.status === 'queued' || latestScan?.status === 'running';
  const perf = useQuery({
    queryKey: ['site', siteId, 'performance', latestScan?.status],
    queryFn: () => unwrap(api.GET('/sites/{siteId}/performance', { params: { path: { siteId } } })),
  });
  const start = useStartScan(siteId, 'performance');
  const latest = perf.data?.latest ?? [];
  // Page by page as in the mock-up: the home page first, each with the phone before the computer.
  const isHome = (url: string) => pageOf(url) === '/';
  const pageOrder = [...new Set(latest.map((s) => s.url))].sort(
    (a, b) => Number(isHome(b)) - Number(isHome(a)),
  );
  const cards = [...latest].sort(
    (a, b) =>
      pageOrder.indexOf(a.url) - pageOrder.indexOf(b.url) ||
      (a.strategy === b.strategy ? 0 : a.strategy === 'mobile' ? -1 : 1),
  );
  const measuredAt = latest
    .map((s) => s.measuredAt)
    .sort()
    .at(-1);
  const measure = (
    <Button size="sm" busy={start.isPending} disabled={running} onClick={() => start.mutate()}>
      {t('speed.measure')}
    </Button>
  );

  return (
    <>
      <div className="hd">
        <div>
          <h1>{t('speed.title')}</h1>
          <div className="sub">
            <span>
              {[
                'Google PageSpeed',
                ...(pages ? [t('speed.pages', { count: pages })] : []),
                ...(measuredAt
                  ? [
                      t('speed.measuredOn', {
                        date: formatDate(measuredAt, i18n.language, timeZone),
                      }),
                    ]
                  : []),
              ].join(' · ')}
            </span>
          </div>
        </div>
        <div className="acts">{measure}</div>
      </div>
      {start.isError ? <Notice kind="error">{scanError(start.error, t)}</Notice> : null}
      {running ? (
        <Notice kind="info">
          {latestScan?.status === 'queued' ? t('speed.queued') : t('speed.running')}
        </Notice>
      ) : null}
      {latestScan?.status === 'failed' && latestScan.errorCode ? (
        <Notice kind="warning">
          {t(`speed.errors.${latestScan.errorCode}`, { defaultValue: t('speed.errors.other') })}
        </Notice>
      ) : null}
      {perf.isPending ? <Loading /> : null}
      {perf.isError ? (
        <LoadError message={errorMessage(perf.error, t)} onRetry={() => void perf.refetch()} />
      ) : null}
      {perf.data && cards.length === 0 ? (
        <div className="empty">
          <b>{t('speed.none')}</b>
          {/* Only before any measurement: a failed one says why in its own message. */}
          {latestScan ? null : <span>{t('speed.noneText')}</span>}
          {running ? null : measure}
        </div>
      ) : null}
      {cards.map((s, n) => (
        <SnapshotCard key={`${s.url}-${s.strategy}`} siteId={siteId} snap={s} first={n === 0} />
      ))}
      {cards.length > 0 ? <p className="spec">{t('speed.source')}</p> : null}
    </>
  );
}
